import React, { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { CloseOutlined, PublicOutlined, StorefrontOutlined } from '@mui/icons-material';
import { fetchData, postData } from '../../../helpers/apiUtils';
import { HubSite } from '../types';
import { SNIPPET_GLOBAL_SITE_ID, SnippetDeployResult, SnippetOwnership } from './types';
import SnippetVorschauDialog from './SnippetVorschauDialog';

const BlocksEditor = React.lazy(() => import('../blocks/BlocksEditor'));

interface Props {
  /** null = geschlossen. */
  snippetId: number | null;
  sites: HubSite[];
  onClose: () => void;
  onChanged: () => void;
}

interface SnippetVoll {
  id: number;
  slug: string;
  site_id: number;
  broker_id: number | null;
  title: string | null;
  content_blocks: string | null;
  revision: number;
  enabled: number;
}

/**
 * Der Inhalts-Editor eines Schnipsels.
 *
 * ⚠ **Die EBENE steht über dem Editor, nicht unter ihm.** Wer die globale Fassung bearbeitet,
 * während ein Portal eine eigene hat, ändert dort nichts und merkt es nicht — das ist die Form
 * des Vorfalls vom 21.08. Die Besitzverhältnisse werden deshalb GELADEN, bevor jemand tippt, und
 * eine fremde Fassung wird als Warnung ausgegeben, nicht als Fussnote.
 */
const SnippetEditorDialog: React.FC<Props> = ({ snippetId, sites, onClose, onChanged }) => {
  const [snippet, setSnippet] = useState<SnippetVoll | null>(null);
  const [ownership, setOwnership] = useState<SnippetOwnership[] | null>(null);
  /**
   * ⚠ Ein EIGENER Zustand fuer „konnte nicht abgefragt werden".
   *
   * Ohne ihn fallen drei Faelle zusammen: „laedt noch", „Abruf gescheitert" und „auf keinem
   * Portal gilt eine andere Fassung" — `ownership` bleibt in allen dreien leer, und
   * `(ownership ?? [])` macht daraus dieselbe leere Liste. Der Dialog BEHAUPTET dann die
   * Abwesenheit einer fremden Fassung, die er nur nicht abfragen konnte. Das ist die Form des
   * Vorfalls vom 21.08. und verstoesst gegen eine Regel, die in `.claude/rules/react-frontend.md`
   * woertlich steht (Gegenlesung 2026-08-25).
   */
  const [besitzFehler, setBesitzFehler] = useState<string | null>(null);
  const [ladefehler, setLadefehler] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  /**
   * ⚠ State fuers Rendern, REF fuer den Waechter — und beide muessen gesetzt werden.
   *
   * Ein Schliessen-Waechter kann im SELBEN Ereignisfluss laufen wie der Schreibvorgang, der das
   * Dokument gerade schmutzig gemacht hat (ein Feld, das beim Verlassen uebernimmt, committet
   * genau dann, wenn man auf X klickt). Der State stuende dort noch auf `false`, der Waechter
   * liesse durch, und die Arbeit waere weg. Dieselbe Konstruktion wie im Profil-Dialog.
   */
  const dirtyRef = useRef(false);
  const setDirtyBeides = useCallback((d: boolean) => {
    dirtyRef.current = d;
    setDirty(d);
  }, []);
  const [verwerfenFragen, setVerwerfenFragen] = useState(false);
  /** Die Vorschau je Zielportal — `null` heisst „Dialog zu" (A40-B4). */
  const [renders, setRenders] = useState<SnippetDeployResult[] | null>(null);
  const [vorschauFehler, setVorschauFehler] = useState<string | null>(null);
  const ladeSchluessel = useRef<string>('');

  /**
   * Schliessen NUR ueber diesen Weg — auch Escape und Klick neben den Dialog.
   *
   * Ohne ihn verwarf jede beilaeufige Schliessgeste das ungespeicherte Dokument, WAEHREND oben
   * „ungespeichert" stand: die Oberflaeche wusste es und tat nichts (Gegenlesung 2026-08-25).
   */
  const schliessenAnfordern = useCallback(() => {
    if (dirtyRef.current) {
      setVerwerfenFragen(true);
      return;
    }
    onClose();
  }, [onClose]);

  /**
   * ⚠ Der Dialog wird OHNE `open &&`-Wächter gerendert, hängt also über die ganze Seitenlebenszeit
   * im Baum: sein State überlebt das Schliessen. Ohne das Leeren VOR dem Abruf stünde beim Öffnen
   * eines anderen Schnipsels für einen Moment der VORIGE unter dem neuen Namen — und der Editor
   * bekäme dessen Dokument als Ausgangsstand. Leeren, dann holen; nie im `.then`.
   */
  useEffect(() => {
    if (snippetId === null) {
      ladeSchluessel.current = '';
      // ⚠ AUCH DIE VORSCHAU RAEUMEN. Dieser Dialog bleibt montiert, wenn er „zu" ist — sein
      // Zustand ueberlebt also das Schliessen. Ohne diese zwei Zeilen zeigte der naechste
      // geoeffnete Schnipsel die Vorschau oder die Fehlermeldung des VORIGEN
      // (Codex-Gegenlesung zu A40-B4).
      setRenders(null);
      setVorschauFehler(null);
      return;
    }
    const key = String(snippetId);
    if (ladeSchluessel.current === key) return;
    ladeSchluessel.current = key;

    let cancelled = false;
    setSnippet(null);
    setOwnership(null);
    setBesitzFehler(null);
    setLadefehler(null);
    setVerwerfenFragen(false);
    // Gleiches beim WECHSEL: `snippetId` springt von 10 auf 11, ohne dazwischen null zu werden.
    setRenders(null);
    setVorschauFehler(null);
    dirtyRef.current = false;
    setDirty(false);

    (async () => {
      const eins = await fetchData<SnippetVoll>(`/api/broker-hub/snippets/${snippetId}`);
      if (cancelled) return;
      if (!eins.success || !eins.data) {
        // Ein Ladefehler ist ein Fehler und NICHT ein leerer Editor: ein leerer Editor über einem
        // gefüllten Schnipsel lädt dazu ein, ihn zu überschreiben.
        setLadefehler(eins.error || 'Der Schnipsel konnte nicht geladen werden.');
        return;
      }
      setSnippet(eins.data);
      const own = await fetchData<SnippetOwnership[]>(
        `/api/broker-hub/snippets/ownership/${encodeURIComponent(eins.data.slug)}`,
      );
      if (cancelled) return;
      if (own.success && own.data) {
        setOwnership(own.data);
      } else {
        // Nicht schweigen: „unbekannt" ist etwas anderes als „nichts da".
        setBesitzFehler(own.error || 'Der Abruf ist fehlgeschlagen.');
      }
    })();

    return () => {
      cancelled = true;
      // ⚠ Muss mit zurück: StrictMode montiert den Effekt im Dev doppelt. Lauf 1 wird abgebrochen,
      // aber der Schlüssel stünde schon — Lauf 2 prallte ab und der Dialog bliebe für immer leer.
      ladeSchluessel.current = '';
    };
  }, [snippetId]);

  const global = snippet ? Number(snippet.site_id) === SNIPPET_GLOBAL_SITE_ID : false;
  const eigenesPortal = snippet
    ? sites.find((s) => Number(s.id) === Number(snippet.site_id))
    : undefined;

  /**
   * Portale, auf denen NICHT diese Fassung gilt — der Grund für die Warnung oben.
   *
   * ⚠ Verglichen wird gegen den GELADENEN Datensatz, nicht gegen das Prop. `snippetId` wird beim
   * Schliessen null, `snippet` bleibt gesetzt (der Ladeeffekt räumt bewusst nicht auf), und MUI
   * rendert den Dialog über die Ausblend-Transition weiter. In diesem Fenster ist
   * `Number(null) === 0`, kein `owner_id` ist 0 — also landeten ALLE Portale hier drin und die
   * Warnung blitzte beim Schliessen auf (Gegenlesung 2026-08-25).
   */
  const fremdBesetzt = (ownership ?? []).filter(
    (o) => o.owner_id !== null && snippet != null && Number(o.owner_id) !== Number(snippet.id),
  );

  /**
   * Eigener Vorschau-Weg — die GANZE Liste, nicht ein Eintrag daraus.
   *
   * ⚠ Bis 2026-08-25 waehlte diese Funktion hier EINE Fassung aus und warf die anderen weg; der
   * Dialog des Editors kann auch nur eine zeigen. Ergebnis: „Gezeigt wird ft." als feste Angabe,
   * kein Umschalter — obwohl der Server seit jeher je Zielportal rendert (A40-B4). Die Auswahl
   * gehoert in den Dialog, nicht in den Ladeweg.
   */
  const vorschauOeffnen = useCallback(
    async (blocks: unknown) => {
      const res = await postData<SnippetDeployResult[]>(
        `/api/broker-hub/snippets/${snippetId}/render-preview`,
        { blocks },
      );
      if (!res.success || !Array.isArray(res.data)) {
        setVorschauFehler(res.error || 'Vorschau fehlgeschlagen');
        return;
      }
      setVorschauFehler(null);
      setRenders(res.data);
    },
    [snippetId],
  );

  return (
    <Dialog open={snippetId !== null} onClose={schliessenAnfordern} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="h6" fontFamily="monospace">
            {snippet?.slug ?? '…'}
          </Typography>
          {snippet && (
            <Tooltip
              title={
                global
                  ? 'Gilt auf allen Portalen — ausser dort, wo es eine eigene Fassung gibt.'
                  : 'Gilt nur auf diesem Portal und verdrängt dort die globale Fassung.'
              }
            >
              <Chip
                size="small"
                icon={global ? <PublicOutlined /> : <StorefrontOutlined />}
                color={global ? 'default' : 'primary'}
                variant="outlined"
                label={global ? 'global' : (eigenesPortal?.name ?? `Portal ${snippet.site_id}`)}
              />
            </Tooltip>
          )}
          {snippet && Number(snippet.enabled) === 0 && (
            <Chip size="small" label="abgeschaltet" />
          )}
          <Box flexGrow={1} />
          {dirty && <Chip size="small" color="warning" label="ungespeichert" />}
          <IconButton onClick={schliessenAnfordern} size="small">
            <CloseOutlined />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {ladefehler && <Alert severity="error">{ladefehler}</Alert>}

        {snippet && besitzFehler && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Die Besitzverhältnisse dieses Namens konnten nicht abgefragt werden ({besitzFehler}).
            Ob auf einem Portal eine ANDERE Fassung gilt, ist damit unbekannt — Änderungen hier
            wirken dort möglicherweise nicht.
          </Alert>
        )}
        {snippet && !besitzFehler && fremdBesetzt.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Auf {fremdBesetzt.map((o) => o.site_key).join(', ')} gilt eine ANDERE Fassung dieses
            Namens. Was du hier änderst, wirkt dort nicht.
          </Alert>
        )}
        {snippet && global && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Globale Fassung: Broker-Fakten lösen gegen die portalübergreifende Ebene auf. Ein nur
            für ein einzelnes Portal gepflegter Wert erscheint deshalb als „nicht hinterlegt“ — er
            würde den Deploy für die übrigen Portale auch blockieren.
          </Alert>
        )}

        {!snippet && !ladefehler && (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        )}

        {snippet && (
          <Suspense
            fallback={
              <Box display="flex" justifyContent="center" py={6}>
                <CircularProgress />
              </Box>
            }
          >
            <BlocksEditor
              key={snippet.id}
              profileId={snippet.id}
              ziel="snippet"
              initialBlocks={snippet.content_blocks}
              revision={Number(snippet.revision)}
              siteKey={eigenesPortal?.site_key ?? null}
              vorschauExtern={vorschauOeffnen}
              onDirtyChange={setDirtyBeides}
              onSaved={(row: any) => {
                setSnippet((vorher) => (vorher ? { ...vorher, revision: Number(row?.revision) } : vorher));
                onChanged();
              }}
            />
          </Suspense>
        )}
      </DialogContent>

      <Dialog open={verwerfenFragen} onClose={() => setVerwerfenFragen(false)}>
        <DialogTitle>Ungespeicherte Aenderungen verwerfen?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Am Inhalt dieses Schnipsels haengen ungespeicherte Aenderungen. Schliessen verwirft
            sie.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVerwerfenFragen(false)}>Weiter bearbeiten</Button>
          <Button
            color="error"
            onClick={() => {
              setVerwerfenFragen(false);
              dirtyRef.current = false;
              setDirty(false);
              onClose();
            }}
          >
            Verwerfen
          </Button>
        </DialogActions>
      </Dialog>

      {/* ⚠ Der Fehlerfall gehoert NEBEN den Dialog, nicht hinein: scheitert der Ladeweg selbst,
          gibt es keine Renders und damit keinen Dialog, in dem etwas stehen koennte. */}
      {vorschauFehler && (
        <Alert severity="error" onClose={() => setVorschauFehler(null)} sx={{ mt: 1 }}>
          {vorschauFehler}
        </Alert>
      )}
      <SnippetVorschauDialog
        renders={renders}
        bevorzugtesPortal={snippet?.site_id ?? null}
        onClose={() => setRenders(null)}
      />
    </Dialog>
  );
};

export default SnippetEditorDialog;
